/*
 * Copyright (c) Castle Point Learning Systems Corporation, 2013-2023, all rights reserved.
 * NOTICE: Dissemination of the information and/or materials contained in this file is strictly
 * forbidden unless prior written permission is obtained from Castle Point Learning Systems Corporation.
 * See LICENSE.txt (part of this package) for the full notice.
 */

import React, { FC, useCallback } from 'react';
import { EvaluationEditor } from './evaluation/EvaluationEditor';
import { Styled as S } from './VerticalSolver.styled';
import { PLACEHOLDER } from './constants';
import { useSessionState } from '../hooks/useSessionState';
import { PersistentEvaluation } from '../types/model';
import { localId } from './evaluation/utils/evaluationUtils';
import { SolverConfig, SolverContextProvider } from './evaluation/context/SolverContextProvider';
import { CompiledProblem } from '../api/types';
import { FlexRow } from '../types/Flex';

interface VerticalSolverProps {
    solutionId: number;
    problem: CompiledProblem;
    config: SolverConfig;
}

export function defaultPersistentEvaluation(): PersistentEvaluation {
    return {
        id: Date.now(),
        lines: [
            {
                id: localId(),
                tree: PLACEHOLDER,
            },
        ],
    };
}

export const VerticalSolver: FC<VerticalSolverProps> = ({ problem, solutionId, config }) => {
    const [evaluations, setEvaluations] = useSessionState<PersistentEvaluation[]>(`VerticalSolver_for_problem_${problem.the_problem.id}`, [
        defaultPersistentEvaluation(),
    ]);
    const handleAddEditor = useCallback(() => setEvaluations((prevState) => prevState.concat(defaultPersistentEvaluation())), [setEvaluations]);
    const handleUpdate = useCallback(
        (evaluation: PersistentEvaluation) => {
            setEvaluations((prevState) => prevState.map((e) => (e.id === evaluation.id ? evaluation : e)));
        },
        [setEvaluations]
    );
    const handleDelete = useCallback(
        (evaluationId: number) =>
            setEvaluations((prevState) => {
                const evaluations = prevState.filter((e) => e.id !== evaluationId);
                return evaluations.length ? evaluations : [defaultPersistentEvaluation()];
            }),
        [setEvaluations]
    );
    const handleClear = useCallback(() => {
        setEvaluations([defaultPersistentEvaluation()]);
    }, [setEvaluations]);
    const handleExitAssistVisMode = useCallback(() => window.dispatchEvent(new KeyboardEvent('keydown', { altKey: true, code: 'KeyA' })), []);

    return (
        <SolverContextProvider problem={problem} config={config}>
            {evaluations.map((e) => (
                <EvaluationEditor key={e.id} solutionId={solutionId} persistentEvaluation={e} onUpdate={handleUpdate} onDelete={handleDelete} />
            ))}
            <FlexRow>
                <S.Add onClick={handleAddEditor}>Click here to create new section</S.Add>
                <S.Clear onClick={handleClear}>Clear solution</S.Clear>
                {config.allowAssistVisualization && <S.Clear onClick={handleExitAssistVisMode}>Exit assist visualization mode</S.Clear>}
            </FlexRow>
        </SolverContextProvider>
    );
};
